import React, { forwardRef, MutableRefObject, ReactNode, Ref, useEffect, useRef, useState } from 'react';
import Select, { ISelectProps } from './Select';
import stripDiacritics from './utils/stripDiacritics';
import isFunction from '../react-data-grid/utils/isFunction';

const defaultCache = {};

//This looks like an unnecessary functional component wrapper; but it is being used like a regular function - Just maintaining existing behaviour.
const defaultChildren = (props: ISelectProps) => {
  return <Select {...props} />;
};

type IAsyncProps = {
  autoload: boolean; // automatically call the `loadOptions` prop on-mount; defaults to true
  cache: any; // object to use to cache results; set to null/false to disable caching
  children: any; // Child function responsible for creating the inner Select component; (props: Object): PropTypes.element
  ignoreAccents: boolean; // strip diacritics when filtering; defaults to true
  ignoreCase: boolean; // perform case-insensitive filtering; defaults to true
  loadingPlaceholder: ReactNode | string;
  loadOptions: Function; // callback to load options asynchronously; (inputValue: string, callback: Function): ?Promise
  multi: boolean; // multi-value input
  options: Array<any>; //array of options;
  placeholder?: ReactNode | string; // field placeholder, displayed when there's no value (shared with Select)
  noResultsText?: ReactNode | string; // field noResultsText, displayed when no options come back from the server
  onChange: Function; // onChange handler: function (newValue) {}
  searchPromptText: ReactNode | string; // label to prompt for search input
  onInputChange?: Function; // optional for keeping track of what is being typed
  value: any; // initial field value
};

const Async = forwardRef((props: ISelectProps & IAsyncProps, ref: Ref<any>) => {
  const {
    autoload = true,
    cache = defaultCache,
    children = defaultChildren,
    loadOptions: loadOptionsFromProps,
    options,
    onChange,
    loadingPlaceholder = 'Loading...',
    searchPromptText = 'Type to search',
    ignoreAccents = true,
    ignoreCase = true,
    placeholder,
    noResultsText: noResultsTextFromProps,
  } = props;

  const _cache = useRef(cache === defaultCache ? {} : cache);
  const _callback = useRef<((error: any, data: any) => void) | null>(null);
  const selectRef = useRef<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState(options ?? []);

  useEffect(() => {
    setAsyncOptions(options ?? []);
  }, [options]);

  useEffect(() => {
    if (autoload) {
      loadOptions('');
    }
  }, []);

  const loadOptions = (inputValue: any) => {
    const cache = _cache.current;

    // eslint-disable-next-line no-prototype-builtins
    if (cache && cache.hasOwnProperty(inputValue)) {
      setAsyncOptions(cache[inputValue]);
      return;
    }

    const callback = (error: any, data?: { options: Array<any> }) => {
      if (callback === _callback.current) {
        _callback.current = null;

        const optionsFromCallback = (data && data.options) || [];

        if (cache) {
          cache[inputValue] = optionsFromCallback;
        }

        setIsLoading(false);
        setAsyncOptions(optionsFromCallback);
      }
    };

    setIsLoading(true);

    // Ignore all but the most recent request
    _callback.current = callback;

    const promise = loadOptionsFromProps(inputValue, callback);
    if (promise) {
      promise.then(
        (data: { options: Array<any> }) => callback(null, data),
        (error: any) => callback(error)
      );
    }

    return inputValue;
  };

  const _onInputChange = (inputValueProp: string) => {
    const { ignoreAccents, ignoreCase, onInputChange } = props;
    let inputValue = inputValueProp;

    if (ignoreAccents) {
      inputValue = stripDiacritics(inputValue);
    }

    if (ignoreCase) {
      inputValue = inputValue.toLowerCase();
    }

    if (onInputChange) {
      onInputChange(inputValue);
    }

    return loadOptions(inputValue);
  };

  const getInputValue = () => {
    if (selectRef.current) {
      return selectRef.current.state.inputValue;
    }
    return '';
  };

  const noResultsText = () => {
    const inputValue = getInputValue();

    if (isLoading) {
      return loadingPlaceholder;
    }
    if (inputValue && noResultsTextFromProps) {
      return noResultsTextFromProps;
    }

    return searchPromptText;
  };

  const selectProps = {
    noResultsText: noResultsText(),
    placeholder: isLoading ? loadingPlaceholder : placeholder,
    options: isLoading && loadingPlaceholder ? [] : asyncOptions,
    ref: (selRef: any) => {
      selectRef.current = selRef;
      if (ref && (ref as MutableRefObject<any>).current) {
        (ref as MutableRefObject<any>).current = selRef;
      } else if (isFunction(ref)) {
        ref(selRef);
      }
    },
    onChange: onChange,
  };

  return children({
    ...props,
    ...selectProps,
    isLoading,
    onInputChange: _onInputChange,
  });
});

export default Async;
